// Generated by Claude Code
import { useTranslation } from 'react-i18next'
import { Copy, TriangleAlert } from 'lucide-react'
import { CardModal } from '#components/primitives/components/card-modal.tsx'
import { FormField } from '#components/primitives/forms/form-field.tsx'
import type { IssuedCredentials } from './api-users-state.ts'

interface ApiUserSecretModalProps {
  issued: IssuedCredentials | null
  onCopy: (value: string) => void
  onClose: () => void
}

/** Shows a freshly issued client id + secret once; closing it discards the secret for good. */
export function ApiUserSecretModal({ issued, onCopy, onClose }: ApiUserSecretModalProps) {
  const { t } = useTranslation('settings')

  const title = issued?.kind === 'rotated'
    ? t('usersTeams.apiUsers.secretModal.rotatedTitle', { name: issued.name })
    : t('usersTeams.apiUsers.secretModal.createdTitle', { name: issued?.name ?? '' })

  return (
    <CardModal
      isOpen={issued !== null}
      title={title}
      onClose={onClose}
      closeOnBackdropClick={false}
      footer={
        <div className="d-flex justify-content-end">
          <button type="button" className="btn btn-primary" onClick={onClose}>
            {t('shared.actions.done')}
          </button>
        </div>
      }
    >
      <div className="alert alert-warning d-flex align-items-start gap-2 py-2">
        <TriangleAlert size={16} className="flex-shrink-0 mt-1" />
        <span className="small">{t('usersTeams.apiUsers.secretModal.warning')}</span>
      </div>
      {issued && (
        <>
          <CopyableValue id="api-user-client-id" label={t('usersTeams.apiUsers.clientId')} value={issued.identifier} onCopy={onCopy} />
          <CopyableValue id="api-user-secret" label={t('usersTeams.apiUsers.clientSecret')} value={issued.secret} onCopy={onCopy} className="mb-0" />
        </>
      )}
    </CardModal>
  )
}

interface CopyableValueProps {
  id: string
  label: string
  value: string
  onCopy: (value: string) => void
  className?: string
}

function CopyableValue({ id, label, value, onCopy, className }: CopyableValueProps) {
  const { t } = useTranslation('settings')
  return (
    <FormField label={label} htmlFor={id} className={className}>
      <div className="d-flex align-items-center gap-2">
        <input
          id={id}
          type="text"
          readOnly
          className="form-control font-monospace"
          value={value}
          onFocus={e => e.currentTarget.select()}
        />
        <button
          type="button"
          className="btn btn-outline-primary d-flex align-items-center gap-1 flex-shrink-0"
          onClick={() => onCopy(value)}
        >
          <Copy size={14} />
          {t('shared.actions.copy')}
        </button>
      </div>
    </FormField>
  )
}
